import { useRef, useEffect } from 'react';
import { useTeamStore } from '@/stores/team-store';

/** Maximum number of path→teamId entries to cache before pruning oldest */
const MAX_PATH_CACHE_SIZE = 50;

/**
 * Stabilizes folderTeamId across transient React re-renders and resolves the
 * effective teamId for GraphQL queries.
 *
 * When navigating into a shared folder, React Router's setSearchParams may race
 * with mode-change re-renders, causing some renders to transiently lose the URL's
 * teamId param.
 *
 * This hook uses a path→teamId map to remember the teamId for each path visited.
 * On second navigation to the same path, even if the URL param isn't available
 * yet, we can use the previously known teamId.
 *
 * The map is limited to MAX_PATH_CACHE_SIZE entries (LRU-style pruning) and
 * cleared on logout (when activeTeamId becomes undefined).
 *
 * Returns the folder's teamId when inside a shared folder, otherwise falls back
 * to the active team from the team store.
 */
export function useEffectiveTeamId(
  currentPath: string | null,
  folderTeamId?: string,
): string | undefined {
  const { activeTeamId } = useTeamStore();

  // Map from path to teamId - remembers teamId for each path visited.
  // Limited to MAX_PATH_CACHE_SIZE entries to prevent unbounded growth.
  const pathTeamIdMapRef = useRef<Map<string, string>>(new Map());

  // Clear cache on logout (activeTeamId becomes undefined)
  const prevActiveTeamIdRef = useRef<string | undefined>(activeTeamId);
  useEffect(() => {
    // Detect logout: activeTeamId changed from defined to undefined
    if (prevActiveTeamIdRef.current && !activeTeamId) {
      pathTeamIdMapRef.current.clear();
    }
    prevActiveTeamIdRef.current = activeTeamId;
  }, [activeTeamId]);

  // Remember the teamId for this path when provided
  if (folderTeamId && currentPath && currentPath !== '/') {
    const map = pathTeamIdMapRef.current;

    // Delete and re-add to make this entry "newest" (Map preserves insertion order)
    map.delete(currentPath);
    map.set(currentPath, folderTeamId);

    // Prune oldest entries if over limit (Map iterates in insertion order)
    while (map.size > MAX_PATH_CACHE_SIZE) {
      const oldestKey = map.keys().next().value;
      if (oldestKey !== undefined) {
        map.delete(oldestKey);
      }
    }
  }

  // For cross-team folders, use URL param first, then remembered value, then activeTeamId
  const rememberedTeamId = currentPath ? pathTeamIdMapRef.current.get(currentPath) : undefined;

  return folderTeamId || rememberedTeamId || activeTeamId;
}
